"use client";

import React from "react";
import { ArrowRight, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MontrealsTrustedShine() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="232870" id="targeted-neighborhoods" className="relative overflow-hidden bg-background pt-24 pb-16 md:pt-32 md:pb-24 lg:pt-40 lg:pb-32">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes float-slow {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-15px); }
            100% { transform: translateY(0px); }
          }
          @keyframes float-delayed {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }
          .animate-float {
            animation: float-slow 6s ease-in-out infinite;
          }
          .animate-float-delayed {
            animation: float-delayed 7s ease-in-out 2s infinite;
          }
        `
    }} />

      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-chart-3/5 blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-7 flex flex-col items-start z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              High-Income Strategy
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-[5rem] leading-[1.05] font-bold font-default tracking-tight text-secondary">
              Precision Targeting for{" "}
              <span className="bg-gradient-to-r from-primary to-chart-3 bg-clip-text text-transparent inline-block">
                Growth
              </span>
            </h1>
            
            <p className="mt-6 text-lg sm:text-xl text-muted-foreground max-w-[600px] leading-relaxed font-default">
              The Peachtree Post connects your business with Metro Atlanta's most affluent households. Our data-driven EDDM routes ensure your message lands in the hands of homeowners with high discretionary income.
            </p>
            
            <div className="mt-10 flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Link to="/ContactPage">
                <Button size="lg" className="w-full sm:w-auto h-14 px-8 text-lg rounded-sm shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300 gap-3 bg-primary text-primary-foreground">
                  Reserve Your Spot
                  <ArrowRight className="size-5" />
                </Button>
              </Link>
              <Link to="/OurServicesPage">
                <Button variant="outline" size="lg" className="w-full sm:w-auto h-14 px-8 text-lg rounded-sm bg-background/50 backdrop-blur-sm border-border hover:bg-accent transition-all duration-300 text-secondary">
                  View Coverage
                </Button>
              </Link>
            </div>
            
            <div className="mt-10 flex items-center gap-4 text-sm text-muted-foreground font-medium">
              <div className="flex -space-x-3">
                {[1, 2, 3].map(i => <div key={i} data-index={i} className="w-10 h-10 rounded-full border-2 border-background bg-muted overflow-hidden">
                    <img src={`/img/atlanta_business_owner_0${i}.png`} alt={`Local Partner ${i}`} className="w-full h-full object-cover" />
                  </div>)}
              </div>
              <p>Trusted by Johns Creek's finest local businesses</p>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 relative h-[500px] sm:h-[600px] w-full mt-8 lg:mt-0">
            {}
            <div className="absolute top-0 right-0 w-[85%] h-[80%] rounded-sm overflow-hidden shadow-2xl shadow-secondary/10 animate-float z-10 border border-border">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/johns_creek_luxury_home.png" alt="Premium residential neighborhood in Johns Creek" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/20 to-transparent mix-blend-overlay"></div>
            </div>

            {}
            <div className="absolute bottom-0 left-0 w-[65%] h-[55%] rounded-sm overflow-hidden shadow-2xl shadow-secondary/15 border-8 border-background animate-float-delayed z-20">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1655661/img/premium_postcard_texture_closeup.png" alt="High-quality co-op postcard detail" className="w-full h-full object-cover" />
            </div>

            {}
            <Card className="absolute top-[20%] -left-4 sm:-left-12 z-30 bg-background/80 backdrop-blur-xl border-border shadow-xl rounded-sm p-4 sm:p-5 flex items-center gap-4 animate-float">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/20 text-primary">
                <Star className="size-6 fill-primary" />
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} data-index={star} className="size-4 fill-primary text-primary" />)}
                </div>
                <p className="font-bold text-secondary font-default leading-none">$150k+ Median Income</p>
                <p className="text-xs text-muted-foreground mt-1">85% Home Ownership Rate</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}